import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from './Constants';

const AttendanceList = ({ enrollId, studentId }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [editingSession, setEditingSession] = useState(null);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateNotes, setUpdateNotes] = useState('');

  const statusOptions = [
    { value: 'all', label: 'All Sessions' },
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'no_show', label: 'No Show' }
  ];

  const statusUpdateOptions = [
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'no_show', label: 'No Show' }
  ];

  // Fetch sessions based on whether enrollId is provided
  useEffect(() => {
    const fetchSessions = async () => {
      try {
        let url = `${API_BASE_URL}/api/sessions`;
        
        // If enrollId is provided, filter sessions by that enrollment
        
        
        const res = await axios.get(url);
        if (enrollId) {
          res.data = res.data.filter(session => session.enrollment_id === enrollId);
        }
        setSessions(res.data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching sessions", err);
        setError("Failed to load sessions");
        setLoading(false);
      }
    };

    fetchSessions();
  }, [enrollId]); // Re-fetch when enrollId changes

  // Calculate filtered sessions based on status filter and search term
  const filteredSessions = sessions.filter(session => {
    // Status filtering
    const statusMatch = filter === 'all' || session.status === filter;
    
    // Search term filtering (only applicable when not filtered by enrollId)
    const searchMatch = enrollId ? true : (searchTerm.trim() === '' || 
      session.enrollment?.student?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      session.enrollment?.course?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      session.meeting_link?.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return statusMatch && searchMatch;
  });

  const getStatusBadge = (status) => {
    const statusConfig = {
      scheduled: { class: 'badge-info', text: 'Scheduled' },
      completed: { class: 'badge-success', text: 'Completed' },
      cancelled: { class: 'badge-error', text: 'Cancelled' },
      no_show: { class: 'badge-warning', text: 'No Show' }
    };
    
    const config = statusConfig[status] || { class: 'badge-neutral', text: status };
    return <span className={`badge ${config.class}`}>{config.text}</span>;
  };

  const formatDateTime = (dateTime) => {
    return new Date(dateTime).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleJoinMeeting = (meetingLink) => {
    if (meetingLink) {
      window.open(meetingLink, '_blank');
    }
  };

  const handleEditStatus = (session) => {
    setEditingSession(session);
    setUpdateNotes(session.notes || '');
  };

  const handleCancelEdit = () => {
    setEditingSession(null);
    setUpdateNotes('');
  };

  const handleUpdateStatus = async (e) => {
    e.preventDefault();
    if (!editingSession) return;

    setUpdateLoading(true);
    try {
      const formData = new FormData(e.target);
      const newStatus = formData.get('status');
      const notes = formData.get('notes');

      const response = await axios.put(
        `${API_BASE_URL}/api/attendence/${editingSession.id}`,
        {
          status: newStatus,
          notes: notes
        }
      );

      // Update the session in the local state
      const updatedSessions = sessions.map(session =>
        session.id === editingSession.id ? response.data : session
      );

      setSessions(updatedSessions);
      setEditingSession(null);
      setUpdateNotes('');

      // Show success message
      alert('Session status updated successfully!');
    } catch (err) {
      console.error("Error updating session", err);
      alert('Failed to update session status');
    } finally {
      setUpdateLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error max-w-4xl mx-auto mt-8">
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      
      {/* Status Update Modal */}
      {editingSession && (
        <div className="modal modal-open">
          <div className="modal-box max-w-2xl">
            <h3 className="font-bold text-lg mb-4">Update Session Status</h3>
            
            <form onSubmit={handleUpdateStatus}>
              <div className="space-y-4">
                {/* Session Info */}
                <div className="bg-base-200 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">Session Details</h4>
                  <p><strong>Course:</strong> {editingSession.enrollment?.course_name || 'N/A'}</p>
                  <p><strong>Student:</strong> {editingSession.enrollment?.student?.name || 'N/A'}</p>
                  <p><strong>Time:</strong> {formatDateTime(editingSession.scheduled_time)}</p>
                  <p><strong>Current Status:</strong> {getStatusBadge(editingSession.status)}</p>
                </div>

                {/* Status Selection */}
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">New Status</span>
                  </label>
                  <select 
                    name="status" 
                    className="select select-bordered w-full"
                    defaultValue={editingSession.status}
                    required
                  >
                    {statusUpdateOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Notes */}
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">Notes (Optional)</span>
                  </label>
                  <textarea 
                    name="notes"
                    className="textarea textarea-bordered h-24"
                    placeholder="Add any notes about this session..."
                    value={updateNotes}
                    onChange={(e) => setUpdateNotes(e.target.value)}
                  />
                </div>

                {/* Action Buttons */}
                <div className="modal-action">
                  <button 
                    type="button" 
                    className="btn btn-ghost"
                    onClick={handleCancelEdit}
                    disabled={updateLoading}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={updateLoading}
                  >
                    {updateLoading ? (
                      <>
                        <span className="loading loading-spinner loading-sm"></span>
                        Updating...
                      </>
                    ) : (
                      'Update Status'
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* <div className="mb-8">
        <h1 className="text-3xl font-bold text-center mb-2">
          {enrollId ? 'Enrollment Sessions' : 'Sessions Management'}
        </h1>
        <p className="text-center text-gray-600">
          {enrollId ? 'View sessions for this enrollment' : 'Manage and view all tutoring sessions'}
        </p>
      </div> */}

      {/* Filters and Search - Hide search when filtered by enrollId */}
      {!enrollId && (
        <div className="card bg-base-100 shadow-xl mb-6">
          <div className="card-body">
            <div className="flex flex-col md:flex-row gap-4 justify-between items-center">
              <div className="flex flex-wrap gap-2">
                {statusOptions.map(option => (
                  <button
                    key={option.value}
                    className={`btn ${filter === option.value ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => setFilter(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              
              <div className="form-control">
                <div className="input-group">
                  <input
                    type="text"
                    placeholder="Search by student, course, or meeting link..."
                    className="input input-bordered"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <button className="btn btn-square">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sessions Count */}
      <div className="stats shadow mb-6 w-full">
        <div className="stat">
          <div className="stat-title">
            {enrollId ? 'Enrollment Sessions' : 'Total Sessions'}
          </div>
          <div className="stat-value">{filteredSessions.length}</div>
          <div className="stat-desc">
            {enrollId ? 'Sessions for this enrollment' : 'Filtered sessions'}
          </div>
        </div>
        {!enrollId && (
          <>
            <div className="stat">
              <div className="stat-title">Scheduled</div>
              <div className="stat-value">
                {sessions.filter(s => s.status === 'scheduled').length}
              </div>
              <div className="stat-desc">All scheduled sessions</div>
            </div>
            <div className="stat">
              <div className="stat-title">Completed</div>
              <div className="stat-value">
                {sessions.filter(s => s.status === 'completed').length}
              </div>
              <div className="stat-desc">All completed sessions</div>
            </div>
          </>
        )}
      </div>

      {/* Sessions List */}
      <div className="grid gap-6">
        {filteredSessions.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📚</div>
            <h3 className="text-xl font-semibold">
              {enrollId ? 'No sessions found for this enrollment' : 'No sessions found'}
            </h3>
            <p className="text-gray-600">
              {enrollId ? 'This enrollment has no sessions yet' : 'Try adjusting your filters or search terms'}
            </p>
          </div>
        ) : (
          filteredSessions.map(session => (
            <div key={session.id} className="card bg-base-100 shadow-xl hover:shadow-2xl transition-shadow">
              <div className="card-body">
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <h2 className="card-title text-2xl">
                        {session.enrollment?.course_name || 'Course Not Found'}
                      </h2>
                      {getStatusBadge(session.status)}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      {!enrollId && (
                        <div>
                          <p className="font-semibold">Student</p>
                          <p className="text-gray-600">{session.enrollment?.student?.name || 'N/A'}</p>
                        </div>
                      )}
                      <div>
                        <p className="font-semibold">Scheduled Time</p>
                        <p className="text-gray-600">{formatDateTime(session.scheduled_time)}</p>
                      </div>
                      <div>
                        <p className="font-semibold">Duration</p>
                        <p className="text-gray-600">{session.duration_minutes} minutes</p>
                      </div>
                      <div>
                        <p className="font-semibold">Meeting Link</p>
                        <p className="text-gray-600 truncate max-w-xs">
                          {session.meeting_link || 'No link provided'}
                        </p>
                      </div>
                    </div>
                    
                    {session.notes && (
                      <div className="mt-3">
                        <p className="font-semibold">Notes</p>
                        <p className="text-gray-600">{session.notes}</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex flex-col gap-2">
                    {session.status === 'scheduled' && session.meeting_link && (
                      <button 
                        className="btn btn-primary btn-sm"
                        onClick={() => handleJoinMeeting(session.meeting_link)}
                      >
                        Join Meeting
                      </button>
                    )}


                    {session.status === 'scheduled' && !studentId && (
                      <button
                        className="btn btn-outline btn-sm"
                        onClick={() => handleEditStatus(session)}
                      >
                        Update Status
                      </button>
                    )}

                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AttendanceList;